import { useState, useEffect, useCallback } from "react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { useEntityManagerContext } from "../../entityManager/context/EntityManagerContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { CardTitle } from "@/modern/components/ui/card";
import { Button as ButtonShadcn } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/modern/components/ui/tooltip";
import { Expand, Plus, X } from "lucide-react";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { TASK_BUDGET_FIELDS } from "@/other/constants/OtherKeys";
import { GRANTS } from "@/other/constants/PrivilegeTypes";

import { BudgetEmptyState } from "./components/BudgetEmptyState";
import { BudgetRadialView } from "./components/BudgetRadialView";

export const EntityBudgetView = () => {
  const { record } = useEntityManagerContext();
  const { project, editorHandler, userPreferences, user } = useSwivuBryntumContext();
  const confirm = useConfirm();
  const [, setUpdateTick] = useState(0);

  const isNewBudgetDisabled = Boolean(
    user &&
      (((user as { isSandboxBoard?: boolean }).isSandboxBoard && user.isDemoUser) || !user.can(GRANTS.grant_two)),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const budgetRecord = (record as any)?.budget ?? null;
  const budgetTitle = budgetRecord?.[TASK_BUDGET_FIELDS.budgetTitleDisplayValue] || budgetRecord?.[TASK_BUDGET_FIELDS.title] || null;

  const forceUpdate = useCallback(() => setUpdateTick((t) => t + 1), []);

  useEffect(() => {
    if (!project?.taskBudgetStore || !budgetRecord) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const detacher = project.taskBudgetStore.on({
      update({ record: updatedRecord }: { record: any }) {
        if (updatedRecord?.id === budgetRecord.id) {
          forceUpdate();
        }
      },
    });
    return () => detacher();
  }, [project, budgetRecord, forceUpdate]);

  const handleOpenEditor = () => {
    if (!budgetRecord) return;
    editorHandler.openEditor(EDITOR_KEYS.budgetEditor, budgetRecord, { budget: budgetRecord });
  };

  const handleAddBudget = () => {
    if (isNewBudgetDisabled) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newRecord = (project.taskBudgetStore as any).createRecord();
    editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, { budget: newRecord });
  };

  const handleDelinkBudget = useCallback(async () => {
    if (!record || !budgetRecord) return;

    const confirmed = await confirm({
      title: "Unlink Budget",
      description: `Are you sure you want to unlink "${budgetTitle || "this budget"}" from this task?`,
      customActions: ({ confirm: onConfirm, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-4">
          <ButtonShadcn variant="ghost" onClick={cancel}>
            Cancel
          </ButtonShadcn>
          <ButtonShadcn
            className="dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors"
            onClick={onConfirm}
          >
            Unlink
          </ButtonShadcn>
        </div>
      ),
    });

    if (confirmed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (record as any).budgetId = null;
      forceUpdate();
    }
  }, [record, budgetRecord, budgetTitle, confirm, forceUpdate]);

  const header = (
    <div className="flex items-center justify-between w-full gap-2">
      <CardTitle className="text-base flex items-center gap-2 min-w-0 truncate">
        <span className="truncate">Budget</span>
      </CardTitle>
      <div className="flex items-center gap-1.5 min-w-0">
        {budgetRecord && budgetTitle && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-semibold min-w-0 overflow-hidden">
            <span className="truncate">{budgetTitle}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelinkBudget();
              }}
              className="shrink-0 rounded-full p-0.5 hover:bg-emerald-500/20 transition-colors"
            >
              <X className="size-3" />
            </button>
          </span>
        )}
        <ButtonGroup>
          {budgetRecord ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <ButtonShadcn variant="entityHeader" className="w-auto px-3" size="icon" onClick={handleOpenEditor}>
                  <Expand className="size-4" /> Manage
                </ButtonShadcn>
              </TooltipTrigger>
              <TooltipContent>Open Budget Editor</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <ButtonShadcn variant="entityHeader" size="icon" disabled={isNewBudgetDisabled} onClick={handleAddBudget}>
                  <Plus className="size-4" />
                </ButtonShadcn>
              </TooltipTrigger>
              <TooltipContent>Add Budget</TooltipContent>
            </Tooltip>
          )}
        </ButtonGroup>
      </div>
    </div>
  );

  return (
    <DashboardCard cardContentClassName="p-0 h-full flex flex-col" header={header}>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {budgetRecord ? (
          <BudgetRadialView budget={budgetRecord} userPreferences={userPreferences} />
        ) : (
          <div className="p-4 h-full">
            <BudgetEmptyState onAddBudget={handleAddBudget} addBudgetDisabled={isNewBudgetDisabled} />
          </div>
        )}
      </div>
    </DashboardCard>
  );
};
